import React, { useEffect, useMemo } from "react";
import { Box, Paper, Typography, Chip, alpha } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { brandColors, brandThemeOptions } from "./brandColors";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../store/store";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { getCookie } from "../../../utils/Utility";
import {
  GetPoliciesData,
  getClaimsData,
} from "../../Dashboard/DashboardAction";
import { getAgents } from "../../Dashboard/Agents/AgentDetailsAction";
import moment from "moment";
import PolicySummaryPanel from "./PolicySummaryPanel";

interface PanelAction {
  id: string;
  label: string;
  params?: any;
  followup_message?: string;
}

interface RightInfoPanelProps {
  card?: any;
  onAction?: (action: PanelAction) => void;
}

const RightInfoPanel: React.FC<RightInfoPanelProps> = ({ card, onAction }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { policiesData, claimsData } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const { agentDetails } = useAppSelector(
    (state: RootState) => state.agentDetails
  );
  const { userDetails } = useAppSelector((state) => state.userAuth);
  const { userSelectedPreferences } = useAppSelector((state) => state.profile);
  const { features } = useAppSelector((state) => state.features);

  const customerId = getCookie("id");
  const carrier = getCookie("carrier");
  const system = getCookie("system");

  useEffect(() => {
    if (customerId === "0987") {
      return;
    }

    if (customerId && carrier && system) {
      dispatch(
        GetPoliciesData({
          customerId,
          carrier,
          system,
        })
      );
      dispatch(
        getClaimsData({
          customerId,
          carrier,
          system,
        })
      );
      dispatch(
        getAgents({
          customerId,
          carrier,
          system,
        })
      );
    }
  }, [customerId, carrier, system, dispatch]);

  const handleViewPolicyDetails = () => {
    const viewPolicyAction: PanelAction = {
      id: "view_policy_details",
      label: "View details",
      followup_message: "Policy Summary"
    };
    onAction?.(viewPolicyAction);
  };

  const handleViewIDCard = () => {
    const viewIDCardAction: PanelAction = {
      id: "view_id_card",
      label: "ID Card",
      followup_message: "ID card"
    };
    onAction?.(viewIDCardAction);
  };

  const handleViewClaimStatus = () => {
    const viewClaimStatusAction: PanelAction = {
      id: "view_claim_status",
      label: "View status",
      followup_message: "Claim Summary"
    };
    onAction?.(viewClaimStatusAction);
  };

  const handlePayNow = () => {
    if (alerts.length > 0) {
      const alert = alerts[0];
      const payNowAction: PanelAction = {
        id: "initiate_payment",
        label: "Pay now",
        params: {
          policy_number: alert.carrierPolicyNo,
          due_amount: alert.dueAmount,
          lob_code: alert.lobCode
        },
        followup_message: "initiate payment"
      };
      onAction?.(payNowAction);
    }
  };

  const openClaimsCount = (claimsData?.claimList || []).filter(
    (c) => c.carrierClaimStatus === "Open"
  ).length;

  const getStatusColors = (status?: string) => {
    const s = (status || "").toLowerCase();
    if (["active", "approved", "paid", "in force"].some((k) => s.includes(k))) {
      return { color: "#15803d", bg: "#dcfce7" };
    }
    if (["open", "submitted", "in progress", "pending", "review"].some((k) => s.includes(k))) {
      return { color: "#b45309", bg: "#fef3c7" };
    }
    if (["closed", "cancelled", "canceled", "expired", "lapsed", "rejected", "denied"].some((k) => s.includes(k))) {
      return { color: "#6b7280", bg: "#f3f4f6" };
    }
    return { color: "#1d4ed8", bg: "#dbeafe" };
  };

  const alerts = useMemo(() => {
    const list: Array<{
      insuredName: string;
      carrierPolicyNo: string;
      dueAmount: string;
      dueDate: string;
      lobCode?: string;
    }> = [];
    if (
      userDetails &&
      policiesData &&
      policiesData.policyList &&
      policiesData.policyList.length > 0 &&
      userSelectedPreferences !== undefined
    ) {
      if (!userSelectedPreferences || userSelectedPreferences.length === 0) {
        policiesData.policyList.forEach((policy: any) => {
          if (
            policy.dueDate &&
            policy.dueAmount &&
            moment(policy.dueDate).diff(moment(), "days") <= 7
          ) {
            list.push({
              insuredName:
                policy?.insuredName && policy?.insuredName.trim() !== ""
                  ? policy?.insuredName.trim()
                  : policy?.insuredList && policy?.insuredList.length > 0
                  ? policy?.insuredList[0]?.businessName || ""
                  : "",
              carrierPolicyNo: policy.carrierPolicyNo,
              dueAmount: policy.dueAmount,
              dueDate: policy.dueDate,
              lobCode: policy?.lobCode,
            });
          }
        });
      } else {
        policiesData.policyList.forEach((policy: any) => {
          if (policy.dueDate && policy.dueAmount) {
            const daysToDueDate = moment(policy.dueDate).diff(moment(), "days");
            const shouldAlert = (userSelectedPreferences || []).some(
              (userPreference: any) =>
                userPreference.preferenceType === "paymentRemainder" &&
                userPreference.preferenceChannel === "pushNotifications" &&
                parseInt(userPreference.duration) >= daysToDueDate
            );
            if (shouldAlert) {
              list.push({
                insuredName:
                  policy?.insuredName && policy?.insuredName.trim() !== ""
                    ? policy?.insuredName.trim()
                    : policy?.insuredList && policy?.insuredList.length > 0
                    ? policy?.insuredList[0]?.businessName || ""
                    : "",
                carrierPolicyNo: policy.carrierPolicyNo,
                dueAmount: policy.dueAmount,
                dueDate: policy.dueDate,
                lobCode: policy?.lobCode,
              });
            }
          }
        });
      }
    }
    return list;
  }, [policiesData, userSelectedPreferences, userDetails]);

  return (
    <ThemeProvider theme={(outer) => createTheme(outer, brandThemeOptions)}>
    <Paper
      elevation={0}
      sx={{
        width: { xs: "100%", md: "40%" },
        border: "1px solid #e5e7eb",
        borderRadius: { xs: 1, md: 2 },
        p: { xs: 1, md: 1.5 },
        bgcolor: "#ffffff",
        overflow: "auto",
        maxHeight: { xs: "90vh", md: "auto" },
        order: { xs: -1, md: 0 },
        // Hide scrollbar by default
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        // Show scrollbar on hover (both horizontal and vertical)
        "&:hover": {
          scrollbarWidth: "auto",
          "&::-webkit-scrollbar": {
            display: "block",
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "4px",
            "&:hover": {
              background: "#a8a8a8",
            },
          },
        },
      }}
    >
      {card ? (
        <PolicySummaryPanel 
          card={card} 
          onAction={onAction}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            gap: { xs: 1, md: 1 },
            overflowX: { xs: "auto", md: "visible" },
            pb: { xs: 1, md: 0 },
              // Hide scrollbar by default
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        // Show scrollbar on hover (both horizontal and vertical)
        "&:hover": {
          scrollbarWidth: "auto",
          "&::-webkit-scrollbar": {
            display: "block",
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "4px",
            "&:hover": {
              background: "#a8a8a8",
            },
          },
        },
          }}
        >
          {features?.some(
            (f: any) => f.featureCode === "alerts" && f.selected
          ) &&
            alerts.length > 0 && (
              <Box
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: { xs: 1, md: 2 },
                  p: { xs: 1, md: 1.5 },
                  bgcolor: "#ffffff",
                  minWidth: { xs: "200px", md: "auto" },
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 12, md: 14 },
                    mb: { xs: 0.5, md: 1 },
                  }}
                >
                  Upcoming bill
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 10, md: 12 },
                      color: "#6b7280",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {alerts[0]?.lobCode
                      ? `${alerts[0]?.lobCode} Policy`
                      : "Policy"}{" "}
                    · {alerts[0]?.carrierPolicyNo}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Box
                    sx={{
                      width: { xs: 6, md: 8 },
                      height: { xs: 6, md: 8 },
                      bgcolor: brandColors.highlight,
                      borderRadius: "50%",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: 10, md: 12 },
                      color: "#374151",
                    }}
                  >
                    ${alerts[0]?.dueAmount} due{" "}
                    {moment(alerts[0]?.dueDate).format("MMM DD")}
                  </Typography>
                </Box>
                {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#6b7280" }}>Method:</Typography>
              <Typography sx={{ fontSize: 12, color: "#374151" }}>Visa ••1234</Typography>
            </Box> */}
                <Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePayNow();
                    }}
                    sx={{
                      fontSize: { xs: 10, md: 12 },
                      py: { xs: 0.5, md: 0.75 },
                      px: { xs: 1, md: 1.5 },
                    }}
                  >
                    Pay now
                  </Button>
                </Box>
              </Box>
            )}

          {/* Hide Agent / Policies / Claims summary for specific demo customer */}
          {customerId !== "0987" && (
            <>
          {/* Your Agent */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "#eceff3",
              borderRadius: { xs: 2, md: 2.5 },
              p: { xs: 1.25, md: 1.75 },
              pl: { xs: 1.75, md: 2.25 },
              bgcolor: "#ffffff",
              minWidth: { xs: "200px", md: "auto" },
              flexShrink: 0,
              transition:
                "box-shadow 200ms ease, border-color 200ms ease, transform 200ms ease",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                bgcolor: "primary.main",
              },
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: (theme) =>
                  `0 8px 22px ${alpha(theme.palette.primary.main, 0.14)}`,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 0.75, md: 1 } }}>
              <Box
                sx={{
                  width: { xs: 26, md: 30 },
                  height: { xs: 26, md: 30 },
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  flexShrink: 0,
                }}
              >
                <SupportAgentOutlinedIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
              </Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: { xs: 12.5, md: 14 }, color: "#111827" }}
              >
                Your Agent
              </Typography>
            </Box>
            {agentDetails && agentDetails.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 11, md: 12.5 },
                    color: "primary.main",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {agentDetails[0]?.nameInfo}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LocalPhoneOutlinedIcon sx={{ fontSize: { xs: 12, md: 13 }, color: "#9ca3af" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: 10.5, md: 12 },
                      color: "#6b7280",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {agentDetails[0]?.phoneInfo}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <MailOutlineIcon sx={{ fontSize: { xs: 12, md: 13 }, color: "#9ca3af" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: 10.5, md: 12 },
                      color: "#6b7280",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {agentDetails[0]?.emailInfo}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: 10.5, md: 12 },
                  color: "#6b7280",
                }}
              >
                No agent found
              </Typography>
            )}
          </Box>

          {/* Policies */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "#eceff3",
              borderRadius: { xs: 2, md: 2.5 },
              p: { xs: 1.25, md: 1.75 },
              pl: { xs: 1.75, md: 2.25 },
              bgcolor: "#ffffff",
              minWidth: { xs: "200px", md: "auto" },
              flexShrink: 0,
              transition:
                "box-shadow 200ms ease, border-color 200ms ease, transform 200ms ease",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                bgcolor: "primary.main",
              },
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: (theme) =>
                  `0 8px 22px ${alpha(theme.palette.primary.main, 0.14)}`,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 0.75, md: 1 } }}>
              <Box
                sx={{
                  width: { xs: 26, md: 30 },
                  height: { xs: 26, md: 30 },
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  flexShrink: 0,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
              </Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: { xs: 12.5, md: 14 }, color: "#111827" }}
              >
                Policies
              </Typography>
              <Chip
                label={policiesData?.policyList?.length || 0}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  "& .MuiChip-label": { px: 0.9 },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {(policiesData?.policyList || []).map((p: any) => {
                const sc = getStatusColors(p.carrierPolicyStatus);
                return (
                <Box
                  key={p.policyId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    py: 0.4,
                    px: 0.75,
                    borderRadius: 1.25,
                    transition: "background-color 150ms ease",
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 11, md: 12.5 },
                      color: "primary.main",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "60%",
                    }}
                  >
                    {p.carrierPolicyNo}
                  </Typography>
                  <Chip
                    label={p.carrierPolicyStatus}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: sc.color,
                      bgcolor: sc.bg,
                      "& .MuiChip-label": { px: 0.9 },
                    }}
                  />
                </Box>
                );
              })}
              {(!policiesData ||
                !policiesData.policyList ||
                policiesData.policyList.length === 0) && (
                <Typography
                  sx={{
                    fontSize: { xs: 10.5, md: 12 },
                    color: "#6b7280",
                  }}
                >
                  No policies found
                </Typography>
              )}
              {policiesData &&
                policiesData.policyList &&
                policiesData.policyList.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 0.5, md: 1 },
                      pt: { xs: 0.75, md: 1.25 },
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={handleViewPolicyDetails}
                      sx={{
                        fontSize: { xs: 10, md: 12 },
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 9999,
                        py: { xs: 0.5, md: 0.75 },
                        px: { xs: 1.5, md: 2 },
                      }}
                    >
                      View details
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={handleViewIDCard}
                      sx={{
                        fontSize: { xs: 10, md: 12 },
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 9999,
                        py: { xs: 0.5, md: 0.75 },
                        px: { xs: 1.5, md: 2 },
                        // borderColor: (theme) => alpha(theme.palette.secondary.main, 0.6),
                        // color: "secondary.main",
                        // "&:hover": {
                        //   borderColor: "secondary.main",
                        //   bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                        // },
                      }}
                    >
                      ID Card
                    </Button>
                  </Box>
                )}
            </Box>
          </Box>

          {/* Claims */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "#eceff3",
              borderRadius: { xs: 2, md: 2.5 },
              p: { xs: 1.25, md: 1.75 },
              pl: { xs: 1.75, md: 2.25 },
              bgcolor: "#ffffff",
              minWidth: { xs: "200px", md: "auto" },
              flexShrink: 0,
              transition:
                "box-shadow 200ms ease, border-color 200ms ease, transform 200ms ease",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                bgcolor: "primary.main",
              },
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: (theme) =>
                  `0 8px 22px ${alpha(theme.palette.primary.main, 0.14)}`,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 0.75, md: 1 }, flexWrap: "wrap" }}>
              <Box
                sx={{
                  width: { xs: 26, md: 30 },
                  height: { xs: 26, md: 30 },
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  flexShrink: 0,
                }}
              >
                <AssignmentOutlinedIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
              </Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: { xs: 12.5, md: 14 }, color: "#111827" }}
              >
                Claims
              </Typography>
              <Chip
                label={claimsData?.claimList?.length || 0}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  "& .MuiChip-label": { px: 0.9 },
                }}
              />
              {openClaimsCount > 0 && (
                <Chip
                  label={`${openClaimsCount} Open`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: brandColors.highlight,
                    bgcolor: brandColors.highlightSoft,
                    "& .MuiChip-label": { px: 0.9 },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {(claimsData?.claimList || []).map((c: any) => {
                const sc = getStatusColors(c.carrierClaimStatus);
                return (
                <Box
                  key={c.claimId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    py: 0.4,
                    px: 0.75,
                    borderRadius: 1.25,
                    transition: "background-color 150ms ease",
                    "&:hover": {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 11, md: 12.5 },
                      color: "primary.main",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "60%",
                    }}
                  >
                    {c.carrierClaimNo}
                  </Typography>
                  <Chip
                    label={c.carrierClaimStatus}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: sc.color,
                      bgcolor: sc.bg,
                      "& .MuiChip-label": { px: 0.9 },
                    }}
                  />
                </Box>
                );
              })}
              {(!claimsData ||
                !claimsData.claimList ||
                claimsData.claimList.length === 0) && (
                <Typography
                  sx={{
                    fontSize: { xs: 10.5, md: 12 },
                    color: "#6b7280",
                  }}
                >
                  No claims found
                </Typography>
              )}
              {claimsData &&
                claimsData.claimList &&
                claimsData.claimList.length > 0 && (
                  <Box
                    sx={{
                      pt: { xs: 0.75, md: 1.25 },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={handleViewClaimStatus}
                      sx={{
                        fontSize: { xs: 10, md: 12 },
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 9999,
                        py: { xs: 0.5, md: 0.75 },
                        px: { xs: 1.5, md: 2 },
                      }}
                    >
                      View details
                    </Button>
                  </Box>
                )}
            </Box>
          </Box>
          </>
          )}
        </Box>
      )}
    </Paper>
    </ThemeProvider>
  );
};

export default RightInfoPanel;
